import type { Program } from 'models';
import { useState } from 'react';
import { usePermissions } from 'permissions';
import { Button, Flex } from 'ui';
import { useUser } from 'features/auth';
import { useGetPriceProperties } from '../../../hooks';
import { CreateRequestToJoin } from '../../../request-to-join';
import { useJoinProgram, useReloadProgram } from './hooks';
import { ProgramDuration } from './program-duration';
import { ProgramPrice } from './program-price';
import { ProgramSponsorList } from './program-sponsor-list';
import { getSubscribeLabel } from './utilities';

type P = {
  program: Program;
};

/**
 * @deprecated This component was only ported to TypeScript as-is as part of #86881, not redesigned. Do not use it as a
 * reference for new code.
 */
const ProgramAboutTopbar = ({ program }: P) => {
  const [isCreateRequestToJoinOpened, setIsCreateRequestToJoinOpened] = useState(false);

  const { railsUserData: viewer } = useUser();
  const { hasUserSentJoinRequest, isMemberOfProgram, joinedAsUser } = usePermissions();
  const getPriceProperties = useGetPriceProperties();
  const { isJoining, joinProgram } = useJoinProgram({ programId: program.id });
  const reloadProgram = useReloadProgram();

  const { isFree, missingToPay, paidBySponsors, totalPrice } = getPriceProperties({
    price: program.price ?? 0,
    priceOption: program.priceOption,
    sponsors: program.sponsors,
  });

  const isContactUs = program.priceOption === 'contact_us';
  const isContactUsVisible = isContactUs && program.author.id !== viewer.id && !joinedAsUser({ program });
  const hasSentJoinRequest = hasUserSentJoinRequest({ program });
  const isSubscribeVisible = !isMemberOfProgram({ program }) && program.status === 'published' && !isContactUs;

  const onSubscribe = () => {
    if (program.privacy === 'private') {
      setIsCreateRequestToJoinOpened(true);
      return;
    }

    joinProgram();
  };

  const onCreateRequestToJoinClose = () => {
    setIsCreateRequestToJoinOpened(false);
  };

  return (
    <Flex align="center" className="program-topbar" justify="space-between">
      {program.topics.length > 0 ? (
        <>
          <ProgramDuration endTime={program.endTime} startTime={program.startTime} />

          <div className="program-topbar__price">
            <ProgramPrice
              isContactUsVisible={isContactUsVisible}
              isFree={isFree}
              missingToPay={missingToPay}
              onContactUsSuccess={reloadProgram}
              paidBySponsors={paidBySponsors}
              program={program}
              totalPrice={totalPrice}
            />
            {program.sponsors ? <ProgramSponsorList price={program.price} sponsors={program.sponsors} /> : null}
          </div>
        </>
      ) : null}
      {isSubscribeVisible ? (
        <>
          <Button
            className="program-topbar__register-button"
            disabled={hasSentJoinRequest}
            loading={isJoining}
            onClick={onSubscribe}
            type="primary"
          >
            {getSubscribeLabel({
              hasUserSentJoinRequest: hasSentJoinRequest,
              isPrivate: program.privacy === 'private',
            })}
          </Button>

          <CreateRequestToJoin
            isOpened={isCreateRequestToJoinOpened}
            onCancel={onCreateRequestToJoinClose}
            onSuccess={onCreateRequestToJoinClose}
            program={program}
          />
        </>
      ) : null}
    </Flex>
  );
};

export default ProgramAboutTopbar;
